import React, {useRef} from 'react';

import {FlipCard} from 'src/components/flip-card/flip-card';

import {motion, useScroll, useTransform} from 'motion/react';
import assetLoader from '@/utils/asset-loader';

import styles from './interactive-features.module.scss';

function InteractiveFeatures() {
    const containerRef = useRef<HTMLDivElement>(null);
    const targetRef = useRef<HTMLDivElement>(null);
    const titleRef = useRef<HTMLDivElement>(null);

    // Main section scroll progress
    const {scrollYProgress} = useScroll({
        target: targetRef,
        offset: ['start end', 'end start'],
    });

    const backgroundX = useTransform(scrollYProgress, [0, 1], [6700, -6800]);
    const backgroundOpacity = useTransform(scrollYProgress, [0, 0.1, 0.99, 1], [0, 1, 1, 0]);

    return (
        <section className={styles.learningSection} ref={containerRef} id="learning-tools">
            <motion.div ref={titleRef} className={styles.sectionTitleContainer}>
                <div className={styles.sectionTitle}>AI-driven learning tools</div>
                <div className={styles.sectionSubtitle}>
                    Meet the future of learning. Preview new and upcoming AI-powered tools to
                    develop your skills, from communicating more effectively to practicing and
                    perfecting presentations, and a whole lot more.
                </div>
            </motion.div>

            <div className={styles.backgroundText}>
                <motion.div
                    style={{x: backgroundX, opacity: backgroundOpacity}}
                    className={styles.marqueeContent}
                >
                    <span className={styles.marqueeText}>
                        <span className={styles.marqueeTextItem}>Role Play</span>
                        <span className={styles.marqueeTextItem}>VR Simulator</span>
                        <span className={styles.marqueeTextItem}>AI Assistant</span>
                    </span>
                </motion.div>
            </div>

            <div className={styles.interactiveFeaturesContainer} ref={targetRef}>
                <motion.div
                    className={styles.interactiveFeature}
                    initial={{opacity: 0, y: 50}}
                    whileInView={{opacity: 1, y: 0}}
                    viewport={{once: true, margin: '-100px'}}
                    transition={{duration: 0.6}}
                >
                    <FlipCard
                        frontTitle={'Role Play'}
                        frontTitleColor={'#000'}
                        backTitle={'Role Play'}
                        number={'01'}
                        backgroundImage={assetLoader({
                            src: '/ci/images/landing/placeholders/cards/webp/1.webp',
                        })}
                        description={
                            'Build essential skills through live practice sessions with an AI coach (friendly avatar included).'
                        }
                        status={'AVAILABLE NOW'}
                    />
                </motion.div>
                <motion.div
                    className={styles.interactiveFeature}
                    initial={{opacity: 0, y: 50}}
                    whileInView={{opacity: 1, y: 0}}
                    viewport={{once: true, margin: '-100px'}}
                    transition={{duration: 0.6}}
                >
                    <FlipCard
                        frontTitle={'VR public speaking simulator'}
                        backTitle={'VR public speaking simulator'}
                        number={'02'}
                        backgroundImage={assetLoader({
                            src: '/ci/images/landing/placeholders/cards/webp/2.webp',
                        })}
                        description={
                            'Deliver your presentation to virtual audiences large and small, so you can take the stage with confidence for the real thing.'
                        }
                        status={'COMING 2026'}
                    />
                </motion.div>
                <motion.div
                    className={styles.interactiveFeature}
                    initial={{opacity: 0, y: 50}}
                    whileInView={{opacity: 1, y: 0}}
                    viewport={{once: true, margin: '-100px'}}
                    transition={{duration: 0.6}}
                >
                    <FlipCard
                        frontTitle={'AI Assistant'}
                        backTitle={'AI Assistant'}
                        number={'03'}
                        backgroundImage={assetLoader({
                            src: '/ci/images/landing/placeholders/cards/webp/3.webp',
                        })}
                        description={
                            'Get immediate answers to your course-related questions from an AI Assistant trained on instructor content.'
                        }
                        status={'AVAILABLE NOW'}
                    />
                </motion.div>
            </div>
        </section>
    );
}

export {InteractiveFeatures};
